import { supabase } from '../supabaseClient';

// Types
export interface Category {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}
export interface Service {
  id: string;
  provider_id: string;
  title: string;
  description: string;
  price: number;
  price_type: 'FIXED' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  category_id: string | null;
  skills: string[];
  delivery_days: number;
  revisions: number;
  features: Array<{
    id: string;
    name: string;
    included: boolean;
  }>;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  featured: boolean;
  views_count: number;
  orders_count: number;
  rating_avg: number;
  rating_cnt: number;
  created_at: string;
  updated_at: string;
}

export interface ServiceWithDetails extends Service {
  provider: {
    id: string;
    full_name: string | null;
    company: string | null;
    avatar_url: string | null;
    rating_avg: number;
    rating_cnt: number;
    verified: boolean;
    location: string | null;
  };
  category: {
    id: string;
    name: string;
    description: string | null;
  } | null;
  reviews?: ServiceReview[];
}

export interface ServiceReview {
  id: string;
  service_id: string;
  order_id: string;
  client_id: string;
  provider_id: string;
  rating_quality: number;
  rating_communication: number;
  rating_delivery: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
  client: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  };
}

export interface ServiceOrder {
  id: string;
  service_id: string;
  client_id: string;
  provider_id: string;
  title: string;
  description: string;
  price: number;
  delivery_days: number;
  status: 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'DELIVERED' | 'COMPLETED' | 'CANCELLED' | 'DISPUTED';
  requirements: string | null;
  attachments: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
  }>;
  delivered_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceFilters {
  search?: string;
  category_id?: string;
  price_min?: number;
  price_max?: number;
  price_type?: string;
  skills?: string[];
  delivery_days?: number;
  featured?: boolean;
  sort_by?: 'created_at' | 'price' | 'rating_avg' | 'orders_count' | 'views_count' | 'price-asc' | 'price-desc' | 'rating_avg-desc' | 'rating_avg-asc' | 'orders_count-desc' | 'created_at-asc' | 'created_at-desc';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Get all active services with filters
export const getServices = async (filters: ServiceFilters = {}): Promise<{
  services: ServiceWithDetails[];
  total: number;
  page: number;
  totalPages: number;
}> => {
  try {
    const {
      search = '',
      category_id,
      price_min,
      price_max,
      price_type,
      skills = [],
      delivery_days,
      featured,
      sort_by = 'created_at',
      sort_order = 'desc',
      page = 1,
      limit = 20
    } = filters;

    let query = supabase
      .from('services')
      .select(`
        *,
        provider:profiles!services_provider_id_fkey(
          id,
          full_name,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified,
          location
        ),
        category:categories(
          id,
          name,
          description
        )
      `)
      .eq('status', 'ACTIVE');

    // Apply filters
    if (search) {
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    }

    if (category_id) {
      query = query.eq('category_id', category_id);
    }

    if (price_min !== undefined) {
      query = query.gte('price', price_min);
    }

    if (price_max !== undefined) {
      query = query.lte('price', price_max);
    }

    if (price_type) {
      query = query.eq('price_type', price_type);
    }

    if (skills.length > 0) {
      query = query.overlaps('skills', skills);
    }

    if (delivery_days !== undefined) {
      query = query.lte('delivery_days', delivery_days);
    }

    if (featured !== undefined) {
      query = query.eq('featured', featured);
    }

    // Apply sorting
    query = query.order(sort_by, { ascending: sort_order === 'asc' });

    // Apply pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching services:', error);
      throw error;
    }

    const total = count || 0;
    const totalPages = Math.ceil(total / limit);

    return {
      services: data || [],
      total,
      page,
      totalPages
    };
  } catch (error) {
    console.error('Error in getServices:', error);
    throw error;
  }
};

// Get service by ID
export const getServiceById = async (serviceId: string): Promise<ServiceWithDetails | null> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select(`
        *,
        provider:profiles!services_provider_id_fkey(
          id,
          full_name,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified,
          location
        ),
        category:categories(
          id,
          name,
          description
        ),
        reviews:service_reviews(
          id,
          client_id,
          rating_quality,
          rating_communication,
          rating_delivery,
          comment,
          created_at,
          client:profiles!service_reviews_client_id_fkey(
            id,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('id', serviceId)
      .single();

    if (error) {
      console.error('Error fetching service:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getServiceById:', error);
    return null;
  }
};

// Create new service
export const createService = async (serviceData: {
  title: string;
  description: string;
  price: number;
  price_type: 'FIXED' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  category_id?: string;
  skills?: string[];
  delivery_days: number;
  revisions?: number;
  features?: Array<{
    id: string;
    name: string;
    included: boolean;
  }>;
}): Promise<Service> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    console.log('Creating service with data:', serviceData);
    console.log('Using user ID:', user.id);

    const { data, error } = await supabase
      .from('services')
      .insert([{
        ...serviceData,
        provider_id: user.id,
        status: 'ACTIVE'
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating service:', error);
      throw error;
    }

    console.log('Service created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error in createService:', error);
    throw error;
  }
};

// Update service
export const updateService = async (serviceId: string, updates: Partial<Service>): Promise<Service> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', serviceId)
      .select()
      .single();

    if (error) {
      console.error('Error updating service:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateService:', error);
    throw error;
  }
};

// Delete service
export const deleteService = async (serviceId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', serviceId);

    if (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteService:', error);
    throw error;
  }
};

// Order service
export const orderService = async (orderData: {
  service_id: string;
  title: string;
  description: string;
  requirements?: string;
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
  }>;
}): Promise<ServiceOrder> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    // Get service details
    const service = await getServiceById(orderData.service_id);
    if (!service) {
      throw new Error('Service not found');
    }

    const { data, error } = await supabase
      .from('service_orders')
      .insert([{
        ...orderData,
        client_id: user.id,
        provider_id: service.provider_id,
        price: service.price,
        delivery_days: service.delivery_days
      }])
      .select()
      .single();

    if (error) {
      console.error('Error ordering service:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in orderService:', error);
    throw error;
  }
};

// Get user's services
export const getUserServices = async (userId?: string): Promise<Service[]> => {
  try {
    let query = supabase
      .from('services')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.eq('provider_id', userId);
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }
      query = query.eq('provider_id', user.id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user services:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserServices:', error);
    return [];
  }
};

// Get user's service orders
export const getUserServiceOrders = async (userId?: string): Promise<ServiceOrder[]> => {
  try {
    let query = supabase
      .from('service_orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.or(`client_id.eq.${userId},provider_id.eq.${userId}`);
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }
      query = query.or(`client_id.eq.${user.id},provider_id.eq.${user.id}`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user service orders:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserServiceOrders:', error);
    return [];
  }
};

// Increment service views
export const incrementServiceViews = async (serviceId: string): Promise<void> => {
  try {
    const { error } = await supabase.rpc('increment_service_views', {
      service_id: serviceId
    });

    if (error) {
      console.error('Error incrementing service views:', error);
      // Don't throw error for analytics
    }
  } catch (error) {
    console.error('Error in incrementServiceViews:', error);
    // Don't throw error for analytics
  }
};

// Get featured services
export const getFeaturedServices = async (limit: number = 6): Promise<ServiceWithDetails[]> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select(`
        *,
        provider:profiles!services_provider_id_fkey(
          id,
          full_name,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified,
          location
        ),
        category:categories(
          id,
          name,
          description
        )
      `)
      .eq('status', 'ACTIVE')
      .eq('featured', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching featured services:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getFeaturedServices:', error);
    return [];
  }
};

// Update service order status
export const updateServiceOrderStatus = async (
  orderId: string, 
  status: ServiceOrder['status']
): Promise<ServiceOrder> => {
  try {
    const { data, error } = await supabase
      .from('service_orders')
      .update({
        status,
        updated_at: new Date().toISOString(),
        ...(status === 'DELIVERED' && { delivered_at: new Date().toISOString() }),
        ...(status === 'COMPLETED' && { completed_at: new Date().toISOString() })
      })
      .eq('id', orderId)
      .select()
      .single();

    if (error) {
      console.error('Error updating service order status:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateServiceOrderStatus:', error);
    throw error;
  }
};

// Get all categories
export const getCategories = async (): Promise<Category[]> => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getCategories:', error);
    return [];
  }
};

// Promotion management functions
export const promoteService = async (serviceId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('services')
      .update({ featured: true })
      .eq('id', serviceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error promoting service:', error);
    throw error;
  }
};

export const unpromoteService = async (serviceId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('services')
      .update({ featured: false })
      .eq('id', serviceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error unpromoting service:', error);
    throw error;
  }
};

export const toggleServicePromotion = async (serviceId: string, featured: boolean): Promise<void> => {
  try {
    const { error } = await supabase
      .from('services')
      .update({ featured })
      .eq('id', serviceId);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling service promotion:', error);
    throw error;
  }
};
